import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  getModbusServerConfig,
  getModbusServerStatus,
  listEdgeGateways,
  restartModbusServer,
  updateModbusServerConfig,
} from "../../../api";
import { GatewayConfirmDialog } from "../../../components/GatewayModal";
import { GatewayMetric, GatewayStatusPill } from "../../../components/GatewayStatus";
import Select from "../../../components/Select";
import { useNotify } from "../../../ToastContext";
import { usePermission } from "../../../UserContext";
import chevronIcon from "../../../assets/chevron-right.svg";
import {
  buildProtocolServerPayload,
  createMapping,
  isWildcardListenHost,
  normalizeModbusServerStatus,
  protocolServerFormFromConfig,
  validateProtocolServerForm,
} from "./plc-protocol-servers-model";
import { FieldError, MappingEditor, NumberField } from "./plc-protocol-server-fields";
import { EdgeModbusServerConfiguration } from "./plc-modbus-edge-server";
import { edgeGatewayIDFromTarget, protocolServerTargetOptions } from "./plc-modbus-edge-server-model";
import { OpcuaServerConfiguration } from "./plc-opcua-server";
import { S7CommServerConfiguration } from "./plc-s7comm-server";

const STATUS_POLL_MS = 5000;

export const PLC_PROTOCOL_WORKSPACES = Object.freeze([
  { id: "modbus", label: "Modbus TCP" },
  { id: "opcua", label: "OPC UA" },
  { id: "s7comm", label: "S7Comm" },
]);

export const plcProtocolWorkspaceFromSearch = (search, fallback = "modbus") => {
  const requested = new URLSearchParams(search || "").get("service");
  if (PLC_PROTOCOL_WORKSPACES.some((item) => item.id === requested)) return requested;
  return PLC_PROTOCOL_WORKSPACES.some((item) => item.id === fallback) ? fallback : "modbus";
};

export const plcProtocolTargetFromSearch = (search) => {
  const requested = String(new URLSearchParams(search || "").get("target") || "").trim();
  return requested === "local" || (requested.startsWith("edge:") && requested.length > 5) ? requested : "local";
};

const DEFAULT_FORM = protocolServerFormFromConfig({
  enabled: false,
  listen_address: "127.0.0.1:1502",
  unit_id: 1,
  max_clients: 16,
  request_timeout_ms: 5000,
  idle_timeout_ms: 300000,
  mappings: [],
});

const errorMessage = (error, fallback) => {
  const message = String(error?.message || error || "").trim();
  return message || fallback;
};

const displayNumber = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "0";

const statusPresentation = (status) => {
  if (!status?.available) {
    return { label: "Runtime unavailable", tone: "unavailable" };
  }
  if (status.running) return { label: "Running", tone: "running" };
  if (status.enabled && status.lastError) {
    return { label: "Needs attention", tone: "error" };
  }
  if (status.enabled) return { label: "Starting", tone: "starting" };
  return { label: "Disabled", tone: "disabled" };
};

const StatusPill = ({ status }) => {
  const presentation = statusPresentation(status);
  return (
    <GatewayStatusPill className={`plcProtocolStatus is-${presentation.tone}`} indicator>
      {presentation.label}
    </GatewayStatusPill>
  );
};

const Metric = ({ label, value, hint }) => (
  <GatewayMetric className="plcProtocolMetric" label={label} value={displayNumber(value)} detail={hint} />
);

export const PlcProtocolServersPage = ({ defaultService = "modbus" }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const routeSearch = `?${searchParams.toString()}`;
  const activeProtocol = plcProtocolWorkspaceFromSearch(routeSearch, defaultService);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [baseline, setBaseline] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [gateways, setGateways] = useState([]);
  const target = plcProtocolTargetFromSearch(routeSearch);
  const [edgeProposal, setEdgeProposal] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // null | { kind: "save" | "restart" }
  const notify = useNotify();
  const canConfigure = usePermission("configure");
  const selectedGatewayID = edgeGatewayIDFromTarget(target);
  const selectedGateway = gateways.find((gateway) => gateway.id === selectedGatewayID) || null;
  const targetOptions = useMemo(() => {
    const options = protocolServerTargetOptions(gateways);
    if (target !== "local" && !options.some((option) => option.value === target)) {
      options.push({ value: target, label: "Unavailable edge runtime" });
    }
    return options;
  }, [gateways, target]);
	const modbusLoadedRef = useRef(false);

	const updateRoute = useCallback((updates) => {
	  const next = new URLSearchParams(searchParams);
	  Object.entries(updates).forEach(([key, value]) => {
		if (value) next.set(key, value);
		else next.delete(key);
	  });
	  setSearchParams(next, { replace: true });
	}, [searchParams, setSearchParams]);

  const payload = useMemo(() => buildProtocolServerPayload(form), [form]);
  const payloadFingerprint = useMemo(() => JSON.stringify(payload), [payload]);
  const dirty = !!baseline && payloadFingerprint !== baseline;
  const baselinePayload = useMemo(() => {
    if (!baseline) return null;
    try {
      return JSON.parse(baseline);
    } catch {
      return null;
    }
  }, [baseline]);
  // Exposure-changing transitions are confirmation-gated; plain edits to an
  // already-open (or already-closed) listener save directly.
  const saveRisk = useMemo(() => {
    if (!baselinePayload) return null;
    if (payload.enabled && !baselinePayload.enabled) return "enable";
    if (payload.enabled && baselinePayload.enabled && payload.listen_address !== baselinePayload.listen_address) {
      return "move";
    }
    if (!payload.enabled && baselinePayload.enabled) return "disable";
    return null;
  }, [payload, baselinePayload]);
  const validation = useMemo(() => validateProtocolServerForm(form), [form]);
  const runtimeAvailable = status?.available === true;
  const configLoaded = !!baseline;
  const editable = runtimeAvailable && configLoaded && canConfigure && !saving && !restarting;

  const load = useCallback(async ({ preserveForm = false, initial = false } = {}) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    const [statusResult, configResult] = await Promise.allSettled([
      getModbusServerStatus(),
      getModbusServerConfig(),
    ]);

    const nextStatus = statusResult.status === "fulfilled"
      ? normalizeModbusServerStatus(statusResult.value)
      : null;
    if (nextStatus) setStatus(nextStatus);

    if (configResult.status === "fulfilled") {
      const nextForm = protocolServerFormFromConfig(configResult.value);
      const nextBaseline = JSON.stringify(buildProtocolServerPayload(nextForm));
      setBaseline(nextBaseline);
      if (!preserveForm) setForm(nextForm);
    }

    const runtimeUnavailable = nextStatus?.available === false;
    if (statusResult.status === "rejected") {
      setLoadError(errorMessage(statusResult.reason, "Could not read Modbus TCP server status."));
    } else if (configResult.status === "rejected" && !runtimeUnavailable) {
      setLoadError(errorMessage(configResult.reason, "Could not load Modbus TCP server configuration."));
    } else {
      setLoadError("");
    }

    setLoading(false);
    setRefreshing(false);
    return nextStatus;
  }, []);

  useEffect(() => {
	if (activeProtocol !== "modbus") return;
	load({ initial: !modbusLoadedRef.current, preserveForm: modbusLoadedRef.current })
	  .finally(() => { modbusLoadedRef.current = true; });
  }, [activeProtocol, load]);

  useEffect(() => {
	if (activeProtocol !== "modbus") return undefined;
    let cancelled = false;
    const loadTargets = async () => {
      try {
        const rows = await listEdgeGateways();
        if (!cancelled) setGateways(Array.isArray(rows) ? rows : []);
      } catch (error) {
        if (!cancelled) notify({ type: "warning", title: "Edge targets unavailable", message: errorMessage(error, "Could not list enrolled runtimes.") });
      }
    };
    loadTargets();
    const timer = window.setInterval(loadTargets, 10000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [activeProtocol, notify]);

  useEffect(() => {
    const handleProposal = (event) => {
      const proposal = event?.detail?.payload;
      const gatewayID = String(proposal?.gateway_id || "");
      if (!gatewayID) return;
	  updateRoute({ service: "modbus", target: `edge:${gatewayID}` });
      setEdgeProposal(proposal);
    };
    window.addEventListener("gw-ai-propose-edge-modbus-server", handleProposal);
    return () => window.removeEventListener("gw-ai-propose-edge-modbus-server", handleProposal);
  }, [updateRoute]);

  useEffect(() => {
	const showOPCUA = () => updateRoute({ service: "opcua" });
    window.addEventListener("gw-ai-propose-opcua-server-role-page", showOPCUA);
    return () => window.removeEventListener("gw-ai-propose-opcua-server-role-page", showOPCUA);
  }, [updateRoute]);

  useEffect(() => {
	if (activeProtocol !== "modbus") return undefined;
    let cancelled = false;
    const timer = window.setInterval(() => {
      getModbusServerStatus()
        .then((next) => {
          if (!cancelled) setStatus(normalizeModbusServerStatus(next));
        })
        .catch(() => {});
    }, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeProtocol]);

  const setField = (field) => (event) => {
    const value = field === "enabled" ? event : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const changeMapping = (index, patch) => {
    setForm((current) => ({
      ...current,
      mappings: current.mappings.map((mapping, candidate) =>
        candidate === index ? { ...mapping, ...patch } : mapping
      ),
    }));
  };

  const removeMapping = (index) => {
    setForm((current) => ({
      ...current,
      mappings: current.mappings.filter((_, candidate) => candidate !== index),
    }));
  };

  const addMapping = () => {
    setForm((current) => ({
      ...current,
      mappings: [...current.mappings, createMapping()],
    }));
  };

  const save = async () => {
    if (!validation.valid) {
      notify({
        type: "error",
        title: "Configuration needs attention",
        message: validation.message,
      });
      return;
    }
    if (saveRisk) {
      setConfirmAction({ kind: "save" });
      return;
    }
    await performSave();
  };

  const performSave = async () => {
    setSaving(true);
    try {
      const updated = await updateModbusServerConfig(payload);
      const nextForm = protocolServerFormFromConfig(updated);
      setForm(nextForm);
      setBaseline(JSON.stringify(buildProtocolServerPayload(nextForm)));
      const nextStatus = await getModbusServerStatus();
      setStatus(normalizeModbusServerStatus(nextStatus));
      notify({
        type: "success",
        title: updated.enabled ? "Modbus TCP server applied" : "Modbus TCP server disabled",
        message: updated.enabled
          ? `Listening on ${updated.listen_address} with ${updated.mappings?.length || 0} mapping${updated.mappings?.length === 1 ? "" : "s"}.`
          : "The listener is disabled and no plant-network port is open.",
      });
    } catch (error) {
      notify({
        type: "error",
        title: "Could not apply server configuration",
        message: errorMessage(error, "The Modbus TCP server rejected the configuration."),
      });
    } finally {
      setSaving(false);
      setConfirmAction(null);
    }
  };

  const restart = async () => {
    if (status?.running) {
      setConfirmAction({ kind: "restart" });
      return;
    }
    await performRestart();
  };

  const performRestart = async () => {
    setRestarting(true);
    try {
      const next = normalizeModbusServerStatus(await restartModbusServer());
      setStatus(next);
      notify({
        type: next.running || !next.enabled ? "success" : "warning",
        title: next.enabled ? "Modbus TCP server restarted" : "Modbus TCP server remains disabled",
        message: next.running
          ? `Listening on ${next.listenAddress}.`
          : (next.lastError || "Enable and save the server before restarting it."),
      });
    } catch (error) {
      notify({
        type: "error",
        title: "Restart failed",
        message: errorMessage(error, "Could not restart the Modbus TCP server."),
      });
    } finally {
      setRestarting(false);
      setConfirmAction(null);
    }
  };

  const confirmDetails = (() => {
    if (!confirmAction) return null;
    if (confirmAction.kind === "restart") {
      return {
        title: "Restart Modbus TCP server?",
        body: `All connected clients on ${status?.listenAddress || "the listener"} are disconnected and must reconnect.`,
        confirmLabel: restarting ? "Restarting…" : "Restart Modbus TCP",
        danger: true,
        busy: restarting,
        onConfirm: performRestart,
      };
    }
    const mappingCount = payload.mappings?.length || 0;
    const writableCount = (payload.mappings || []).filter((mapping) =>
      ["coils", "holding_registers"].includes(mapping.table)
    ).length;
    if (saveRisk === "enable") {
      const wildcard = isWildcardListenHost(form.listenHost);
      return {
        title: "Open Modbus TCP listener?",
        body: `${wildcard ? `${form.listenHost} listens on every network interface of this device. ` : ""}Saving opens ${payload.listen_address} to Modbus clients: ${mappingCount} tag mapping${mappingCount === 1 ? "" : "s"} become${mappingCount === 1 ? "s" : ""} readable${writableCount ? ` and ${writableCount} accept${writableCount === 1 ? "s" : ""} writes` : ""}.`,
        confirmLabel: saving ? "Saving…" : "Open listener",
        danger: wildcard,
        busy: saving,
        onConfirm: performSave,
      };
    }
    if (saveRisk === "move") {
      return {
        title: "Move Modbus TCP listener?",
        body: `The listener moves from ${baselinePayload?.listen_address} to ${payload.listen_address}. Clients connected to the old address are dropped and must reconnect to the new one.`,
        confirmLabel: saving ? "Saving…" : "Move listener",
        danger: false,
        busy: saving,
        onConfirm: performSave,
      };
    }
    return {
      title: "Stop Modbus TCP server?",
      body: `Saving disables the server: connected clients are disconnected and ${status?.listenAddress || payload.listen_address} closes.`,
      confirmLabel: saving ? "Saving…" : "Stop server",
      danger: true,
      busy: saving,
      onConfirm: performSave,
    };
  })();

  const refresh = async () => {
    await load({ preserveForm: dirty });
    if (dirty) {
      notify({
        type: "info",
        title: "Live status refreshed",
        message: "Your unsaved server edits were kept.",
      });
    }
  };

  const metrics = status?.metrics || {};
  const showMetrics = Boolean(status?.running || [
    metrics.activeClients,
    metrics.acceptedClients,
    metrics.rejectedClients,
    metrics.requests,
    metrics.exceptions,
    metrics.transportErrors,
  ].some((value) => Number(value) > 0));
  const wildcardWarning = form.enabled && isWildcardListenHost(form.listenHost);
  const pageCopy = activeProtocol === "opcua"
    ? {
        title: "OPC UA server",
        subtitle: "Allow external OPC UA clients to connect to Control Seat.",
      }
    : activeProtocol === "s7comm"
      ? {
          title: "S7Comm server",
          subtitle: "Allow Siemens S7 clients to read or write selected Control Seat tags.",
        }
      : {
          title: "Modbus TCP server",
          subtitle: "Allow external Modbus clients to read or write selected Control Seat tags.",
        };

  return (
    <div className="gwContentGrid" data-gw-resource="modbus_server">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">{pageCopy.title}</div>
            <div className="gwPageSubtitle">{pageCopy.subtitle}</div>
          </div>
          {/* Refresh and Restart act on the whole server, not on the Status
              card they used to live inside. In a card heading they competed
              with the section label and the state pill for one 30px strip. */}
          {activeProtocol === "modbus" && target === "local" && status ? (
            <div className="gwHeaderActions">
              <button
                className="gwSecondaryBtn"
                type="button"
                disabled={refreshing || loading}
                onClick={refresh}
              >
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
              <button
                className="gwSecondaryBtn"
                type="button"
                disabled={!runtimeAvailable || !status?.enabled || !canConfigure || restarting || saving}
                onClick={restart}
                title={!canConfigure
                  ? "Configure permission required"
                  : !status?.enabled ? "Turn on and save the server before restarting it" : "Restart the Modbus TCP server"}
              >
                {restarting ? "Restarting…" : "Restart"}
              </button>
            </div>
          ) : null}
        </div>

		<div
		  className="plcProtocolWorkspacePanel"
		  hidden={activeProtocol !== "modbus"}
		>

        <div className="plcProtocolTargetBar" data-gw-resource="modbus_server_target">
          <label className="gwLabel">Runs on</label>
          <Select value={target} onChange={(value) => { updateRoute({ target: value }); setEdgeProposal(null); }} options={targetOptions} />
          <span className="gwHint">Choose the computer that will accept incoming Modbus connections.</span>
        </div>

        {target === "local" ? <>
        {loading ? <div className="gwHint">Loading Modbus TCP…</div> : null}

        {loadError ? (
          <div className="gwAlert" role="alert">
            <div className="gwAlertTitle">Modbus TCP is unavailable</div>
            <div className="gwAlertBody">Refresh the page or choose another runtime.</div>
          </div>
        ) : null}

        {!loading && status && !runtimeAvailable ? (
          <div className="plcProtocolRuntimeNotice" role="note">
            <strong>Choose a runtime</strong>
            <span>
              Select the computer that should accept incoming Modbus connections.
            </span>
          </div>
        ) : null}

        {status ? (
          <div className="gwCardSection">
            <div className="gwSectionTitle plcProtocolSectionTitle">
              <span>Status</span>
              <StatusPill status={status} />
            </div>
            <div className="gwCardSectionBody">
              <div className="plcProtocolRuntimeSummary">
                <div>
                  <span>Address</span>
                  <strong>{status.listenAddress || "Not configured"}</strong>
                </div>
                <div>
                  <span>Unit ID</span>
                  <strong>{status.unitId}</strong>
                </div>
                <div>
                  <span>Exposed tags</span>
                  <strong>{status.mappingCount}</strong>
                </div>
                <div>
                  <span>Active clients</span>
                  <strong>{displayNumber(metrics.activeClients)}</strong>
                </div>
              </div>
              {status.lastError && runtimeAvailable ? (
                <div className="plcProtocolRuntimeError" role="alert">
                  <strong>The server could not start</strong>
                  <span>Check the network address, port, and allowed client networks, then try again.</span>
                </div>
              ) : null}
              {showMetrics ? (
                <details className="plcProtocolDetails">
                  <summary>
                    <span>Activity and errors</span>
                    <small>{displayNumber(metrics.activeClients)} active · {displayNumber(metrics.requests)} requests</small>
                    <img src={chevronIcon} alt="" className="plcProtocolDetailsChevron" />
                  </summary>
                  <div className="plcProtocolDetailsBody">
                    <div className="plcProtocolMetrics" aria-label="Modbus TCP server metrics">
                      <Metric label="Active clients" value={metrics.activeClients} />
                      <Metric label="Accepted clients" value={metrics.acceptedClients} />
                      <Metric label="Rejected clients" value={metrics.rejectedClients} />
                      <Metric label="Requests" value={metrics.requests} />
                      <Metric label="Protocol exceptions" value={metrics.exceptions} />
                      <Metric label="Transport errors" value={metrics.transportErrors} />
                    </div>
                  </div>
                </details>
              ) : null}
            </div>
          </div>
        ) : null}

        {runtimeAvailable && configLoaded ? (
          <>
            <div className="gwCardSection">
              <div className="gwSectionTitle plcProtocolSectionTitle">
                <span>Server settings</span>
                {dirty ? <span className="plcProtocolUnsaved">Unsaved changes</span> : null}
              </div>
              <div className="gwCardSectionBody">
                <div className="gwToggleRow plcProtocolEnableRow">
                  <div>
                    <div className="gwLabel">Enable Modbus TCP server</div>
                    <div className="gwHint">
                      Turn this on to let Modbus clients connect after you save.
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`gwToggle ${form.enabled ? "isOn" : ""} ${!editable ? "isDisabled" : ""}`}
                    role="switch"
                    aria-checked={form.enabled}
                    aria-label="Enable Modbus TCP server"
                    disabled={!editable}
                    onClick={() => setField("enabled")(!form.enabled)}
                  >
                    <span />
                  </button>
                </div>

                <div className="plcProtocolConfigGrid">
                  <div className="gwField plcProtocolListenHost">
                    <label className="gwLabel">Network address</label>
                    <input
                      className="gwInput"
                      value={form.listenHost}
                      onChange={setField("listenHost")}
                      placeholder="127.0.0.1"
                      disabled={!editable}
                      aria-invalid={!!validation.formErrors.listenHost}
                    />
                    <div className="gwHint">
                      Use the address on the network your Modbus clients can reach.
                    </div>
                    <FieldError>{validation.formErrors.listenHost}</FieldError>
                  </div>
                  <NumberField
                    label="TCP port"
                    value={form.listenPort}
                    onChange={setField("listenPort")}
                    min={1}
                    max={65535}
                    step={1}
                    disabled={!editable}
                    hint="Port 502 is the Modbus TCP default."
                    error={validation.formErrors.listenPort}
                  />
                  <NumberField
                    label="Unit ID"
                    value={form.unitId}
                    onChange={setField("unitId")}
                    min={0}
                    max={255}
                    step={1}
                    disabled={!editable}
                    hint="Clients must use this unit ID."
                    error={validation.formErrors.unitId}
                  />
                </div>

                <details className="plcProtocolDetails">
                  <summary>
                    <span>Connection limits</span>
                    <small>{form.maxClients} clients · {form.requestTimeoutMs} ms request timeout</small>
                    <img src={chevronIcon} alt="" className="plcProtocolDetailsChevron" />
                  </summary>
                  <div className="plcProtocolDetailsBody plcProtocolConfigGrid">
                    <NumberField
                      label="Maximum clients"
                      value={form.maxClients}
                      onChange={setField("maxClients")}
                      min={1}
                      max={1024}
                      step={1}
                      disabled={!editable}
                      hint="Additional connections are rejected and counted."
                      error={validation.formErrors.maxClients}
                    />
                    <NumberField
                      label="Request timeout (ms)"
                      value={form.requestTimeoutMs}
                      onChange={setField("requestTimeoutMs")}
                      min={100}
                      max={60000}
                      step={100}
                      disabled={!editable}
                      hint="Bounds idle and incomplete protocol requests."
                      error={validation.formErrors.requestTimeoutMs}
                    />
                    <NumberField
                      label="Idle client timeout (ms)"
                      value={form.idleTimeoutMs}
                      onChange={setField("idleTimeoutMs")}
                      min={1000}
                      max={3600000}
                      step={1000}
                      disabled={!editable}
                      hint="Closes idle client slots instead of allowing them to remain parked indefinitely."
                      error={validation.formErrors.idleTimeoutMs}
                    />
                  </div>
                </details>

                <div className="gwField plcProtocolSourceCidrs">
                  <label className="gwLabel">Allowed client networks</label>
                  <textarea
                    className="gwInput"
                    rows={3}
                    value={form.sourceCidrsText}
                    onChange={setField("sourceCidrsText")}
                    placeholder={"10.0.0.0/24\n192.168.1.5/32"}
                    disabled={!editable}
                    aria-invalid={!!validation.formErrors.sourceCidrs}
                  />
                  <div className="gwHint">
                    One network per line. Only clients on these networks can connect.
                  </div>
                  <FieldError>{validation.formErrors.sourceCidrs}</FieldError>
                </div>

                {wildcardWarning ? (
                  <div className="plcProtocolExposureWarning" role="note">
                    <strong>All network interfaces selected</strong>
                    <span>
                      {form.listenHost} exposes mapped process values on every reachable interface. Prefer the specific plant-network IP and enforce network segmentation before enabling.
                    </span>
                  </div>
                ) : null}

                {!canConfigure ? (
                  <div className="gwNotice isSoft">Configure permission is required to change or restart this server.</div>
                ) : null}
              </div>
            </div>

            <div className="gwCardSection">
              <div className="gwSectionTitle plcProtocolSectionTitle">
                <div>
                  <span>Exposed tags</span>
                  <small>{form.mappings.length} mapping{form.mappings.length === 1 ? "" : "s"}</small>
                </div>
                <button
                  className="gwSecondaryBtn plcProtocolAddButton"
                  type="button"
                  disabled={!editable}
                  onClick={addMapping}
                >
                  Add mapping
                </button>
              </div>
              <div className="gwCardSectionBody">
                <div className="gwHint plcProtocolMappingHint">
                  Match Control Seat tags to the Modbus addresses external clients will use.
                </div>

                {form.mappings.length === 0 ? (
                  <div className="gwEmptyState gwEmptyCompact">
                    <div className="gwEmptyTitle">No tags are shared</div>
                    <div className="gwEmptyBody">Add a mapping to expose a tag through Modbus TCP.</div>
                  </div>
                ) : (
                  <div className="plcProtocolMappings">
                    {form.mappings.map((mapping, index) => (
                      <MappingEditor
                        key={mapping.key}
                        mapping={mapping}
                        index={index}
                        error={validation.mappingErrors[index]}
                        disabled={!editable}
                        onChange={changeMapping}
                        onRemove={removeMapping}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Page-level commit, not a footer of the last card. It applies the
                server settings and the exposed tags in one write, so it must
                sit outside both cards — nested in "Exposed tags" it read as an
                action belonging only to the mapping list. */}
            <div className="plcProtocolSaveRow">
              <div>
                <strong>{validation.valid ? "Ready to save" : "Configuration needs attention"}</strong>
                <span>
                  {validation.valid
                    ? dirty
                      ? "Applies the server settings and exposed tags together."
                      : "No changes to apply."
                    : validation.message}
                </span>
              </div>
              <button
                className="gwPrimaryBtn"
                type="button"
                disabled={!editable || !dirty || !validation.valid}
                onClick={save}
              >
                {saving ? "Saving…" : "Save configuration"}
              </button>
            </div>
          </>
        ) : null}
        </> : selectedGateway ? (
          <EdgeModbusServerConfiguration gateway={selectedGateway} canConfigure={canConfigure} initialProposal={edgeProposal} />
        ) : (
          <div className="gwAlert" role="alert"><div className="gwAlertTitle">Edge runtime not found</div><div className="gwAlertBody">The selected runtime is no longer enrolled. Choose another execution target.</div></div>
        )}
		</div>

		<div
		  className="plcProtocolWorkspacePanel"
		  hidden={activeProtocol !== "opcua"}
		>
		  <OpcuaServerConfiguration active={activeProtocol === "opcua"} availabilityOnly />
		</div>
		<div
		  className="plcProtocolWorkspacePanel"
		  hidden={activeProtocol !== "s7comm"}
		>
		  <S7CommServerConfiguration active={activeProtocol === "s7comm"} showHeader={false} />
		</div>
      </section>
      {confirmDetails ? (
        <GatewayConfirmDialog
          title={confirmDetails.title}
          body={confirmDetails.body}
          confirmLabel={confirmDetails.confirmLabel}
          danger={confirmDetails.danger}
          busy={confirmDetails.busy}
          onCancel={() => {
            if (!confirmDetails.busy) setConfirmAction(null);
          }}
          onConfirm={confirmDetails.onConfirm}
        />
      ) : null}
    </div>
  );
};
